import { Box, Flex, useControllableState } from "@chakra-ui/react"
import { ChevronDownIcon } from "Icons/ChevronDownIcon"
import { ChevronRightIcon } from "Icons/ChevronRightIcon"
import { AnimatePresence, motion } from "framer-motion"
import React, { forwardRef } from "react"
import { Heading } from "../Heading"
import { IconButton } from "../IconButton"

type AreaProps = {
  children: React.ReactNode
  heading?: string
  accentIcon?: React.ReactNode
  controls?: React.ReactNode
  isCollapsed?: boolean
  onCollapsedChange?: (isCollapsed: boolean) => void
  showCollapseButton?: boolean
}

/**
 * See Layout.Area (which exports this component) for documentation.
 */
export const Area = forwardRef<HTMLDivElement, AreaProps>(
  (
    {
      children,
      heading,
      accentIcon,
      controls,
      isCollapsed: controlledIsCollapsed,
      onCollapsedChange,
      showCollapseButton = false,
    },
    ref
  ) => {
    const [isCollapsed, setIsCollapsed] = useControllableState({
      value: controlledIsCollapsed,
      defaultValue: false,
      onChange: (isCollapsed) => {
        onCollapsedChange?.(isCollapsed)
      },
    })

    return (
      <Flex
        ref={ref}
        flexDirection="column"
        gap={6}
        data-ui="layout-area"
        sx={{
          // This is available to all children to use as necessary.
          // It represents the vertical gap between direct children of an Area.
          "--layout-area-spacing-gap": "1.5rem",
        }}
      >
        <Flex
          gap={4}
          justifyContent={
            controls && !heading && !accentIcon ? "flex-end" : "space-between"
          }
        >
          {accentIcon}
          {heading && (
            <Heading textStyle="ds.display.area" as="h2" flexGrow={1}>
              {heading}
            </Heading>
          )}
          {controls}
          {isCollapsed !== undefined && showCollapseButton && (
            <ShowHideControls
              isCollapsed={isCollapsed}
              onCollapsedChange={() => setIsCollapsed(!isCollapsed)}
            />
          )}
        </Flex>
        <MaybeAnimatePresence isOpen={!isCollapsed}>
          {children}
        </MaybeAnimatePresence>
      </Flex>
    )
  }
)

export const Areas: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Flex flexDirection="column" gap={10}>
    {children}
  </Flex>
)

const ShowHideControls: React.FC<{
  isCollapsed: boolean
  onCollapsedChange: (isCollapsed: boolean) => void
}> = ({ isCollapsed, onCollapsedChange }) => {
  const buttonIcon = isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />

  return (
    <IconButton
      aria-label={isCollapsed ? "show" : "hide"}
      icon={buttonIcon}
      variant="secondary"
      onClick={() => onCollapsedChange(!isCollapsed)}
    />
  )
}

const MaybeAnimatePresence: React.FC<{
  isOpen?: boolean
  children: React.ReactNode
}> = ({ isOpen, children }) => {
  if (isOpen !== undefined)
    return (
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            variants={{
              collapsed: {
                opacity: 0,
                height: 0,
                transition: {
                  opacity: { duration: 0.15 },
                  height: { duration: 0.2 },
                },
                overflow: "hidden",
              },
              expanded: {
                opacity: 1,
                height: "auto",
                transition: {
                  opacity: { duration: 0.15 },
                  height: { duration: 0.2 },
                },
                overflow: "unset",
              },
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    )

  return <Box>{children}</Box>
}
